/** A file chosen for upload, with the path it was selected under. */
export interface SelectedUpload {
  readonly file: File;
  /** Path relative to the selected root; the bare filename for loose files. */
  readonly relativePath: string;
}

/** File and folder pickers report the selected folder path on the file itself. */
export function selectedFromPicker(files: Iterable<File>): SelectedUpload[] {
  return Array.from(files, file => ({ file, relativePath: file.webkitRelativePath || file.name }));
}

function readDirectory(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => { reader.readEntries(resolve, reject); });
}

function readFile(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => { entry.file(resolve, reject); });
}

// Directory entries expose these native discriminants even when the browser
// does not expose FileSystemFileEntry/FileSystemDirectoryEntry constructors.
function isFileEntry(entry: FileSystemEntry): entry is FileSystemFileEntry {
  return entry.isFile;
}

function isDirectoryEntry(entry: FileSystemEntry): entry is FileSystemDirectoryEntry {
  return entry.isDirectory;
}

async function walkEntry(entry: FileSystemEntry, selected: SelectedUpload[], limit: number): Promise<void> {
  if (selected.length > limit) return;
  if (isFileEntry(entry)) {
    selected.push({ file: await readFile(entry), relativePath: entry.fullPath.replace(/^\//u, '') });
    return;
  }
  if (!isDirectoryEntry(entry)) return;
  const reader = entry.createReader();
  while (selected.length <= limit) {
    const children = await readDirectory(reader);
    if (children.length === 0) break;
    for (const child of children) {
      await walkEntry(child, selected, limit);
      if (selected.length > limit) break;
    }
  }
}

/** Read every directory page, stopping with an over-limit error rather than truncating silently. */
export async function filesFromDrop(data: DataTransfer, limit: number): Promise<SelectedUpload[]> {
  const entries = Array.from(data.items, item => item.webkitGetAsEntry()).filter(entry => entry !== null);
  const selected = entries.length === 0 ? selectedFromPicker(data.files) : [];
  for (const entry of entries) {
    await walkEntry(entry, selected, limit);
    if (selected.length > limit) break;
  }
  if (selected.length > limit) throw new Error(`This selection contains more than ${limit.toLocaleString('en')} files. Choose a smaller folder; nothing was added.`);
  return selected;
}

/** The project folder a selected file lands in: the target folder plus its selected parent path. */
export function selectedFolderPath(relativePath: string, destination: string | null): string | null {
  const parent = relativePath.includes('/') ? relativePath.slice(0, relativePath.lastIndexOf('/')) : '';
  return [destination, parent].filter(Boolean).join('/') || null;
}
